import { z } from "zod";
import { isoDateTimeSchema } from "../ids";
import { addressSchema, localDateSchema } from "./common";

/**
 * SCH-19..SCH-22: the public booking page. Everything here is what a customer sees without
 * signing in, so it carries no internal ids other than what they need to pick and book.
 */

export const publicServiceSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /** field: we come to you (ask for the address); appointment: you come to us (pick a location). */
  mode: z.enum(["field", "appointment"]),
  durationMin: z.number().int(),
  /** Minutes of arrival window; 0 = an exact time. */
  windowMin: z.number().int(),
  priceCents: z.number().int().nullable(),
  /** Deposit taken at booking, in cents (0 = none). */
  depositCents: z.number().int(),
});
export type PublicService = z.infer<typeof publicServiceSchema>;

export const publicLocationSchema = z.object({
  id: z.string(),
  name: z.string(),
  addressLine: z.string().nullable(),
});

export const bookingConfigSchema = z.object({
  businessName: z.string(),
  businessPhone: z.string().nullable(),
  timezone: z.string(),
  /** False when the business turned online booking off or is paused. */
  open: z.boolean(),
  services: z.array(publicServiceSchema),
  locations: z.array(publicLocationSchema),
  /** First and last local dates that can be booked. */
  firstDate: localDateSchema,
  lastDate: localDateSchema,
  /** Cloudflare Turnstile site key for the bot check (null in tests). */
  turnstileSiteKey: z.string().nullable(),
});
export type BookingConfig = z.infer<typeof bookingConfigSchema>;

export const publicSlotsInputSchema = z.object({
  serviceId: z.string().min(1),
  from: localDateSchema,
  to: localDateSchema,
  /** Field mode: where the work is, so drive time is counted. */
  address: addressSchema.optional(),
  locationId: z.string().optional(),
});
export type PublicSlotsInput = z.input<typeof publicSlotsInputSchema>;

export const publicSlotSchema = z.object({
  start: isoDateTimeSchema,
  windowStart: isoDateTimeSchema,
  windowEnd: isoDateTimeSchema,
});
export const publicSlotsSchema = z.object({ slots: z.array(publicSlotSchema) });

export const publicHoldInputSchema = z.object({
  serviceId: z.string().min(1),
  start: isoDateTimeSchema,
  address: addressSchema.optional(),
  locationId: z.string().optional(),
  /** The time this person held before (went back and picked again): let it go. */
  replaces: z.string().max(64).optional(),
  /** Cloudflare Turnstile token from the page. */
  turnstileToken: z.string().max(4096).optional(),
});
export type PublicHoldInput = z.input<typeof publicHoldInputSchema>;
export const publicHoldSchema = z.object({
  holdId: z.string(),
  expiresAt: isoDateTimeSchema,
});

export const publicBookInputSchema = z.object({
  holdId: z.string().min(1),
  firstName: z.string().trim().min(1).max(80),
  lastName: z.string().trim().max(80).optional(),
  phone: z
    .string()
    .trim()
    .max(30)
    .refine((v) => v.replace(/\D/g, "").length >= 10, "Enter a phone number with area code."),
  email: z.string().trim().email().optional(),
  address: addressSchema.optional(),
  notes: z.string().max(1000).optional(),
});
export type PublicBookInput = z.input<typeof publicBookInputSchema>;

export const publicBookingResultSchema = z.object({
  jobNumber: z.number().int(),
  /** "Tue, Oct 6, between 8 and 10 AM" in the business's time zone. */
  when: z.string(),
  serviceName: z.string(),
  /** Pay the deposit here now (CRM-20); null when the service takes none. */
  paymentUrl: z.string().nullable(),
  depositCents: z.number().int(),
  replayed: z.boolean(),
});
export type PublicBookingResult = z.infer<typeof publicBookingResultSchema>;
